import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Divider, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconCalendarCheck, IconClock, IconPencil, IconSend } from '@tabler/icons-react';
import ScheduleCalendar from '@/components/app/deals/steps/ScheduleCalendar';
import { useConfirmSchedule, useSuggestSchedule } from '@/hooks/useDeals';
import { isoToRu } from '@/lib/date';
import { isoToSchedule, scheduleRange, schedulePeriod, scheduleToIso } from '@/lib/schedule';
import type { BargainStepsInfo } from '@/types/deals';

interface ShipmentScheduleStepProps {
  readonly dealId: number;
  readonly info: BargainStepsInfo;
}

const SENDABLE_STEPS = new Set(['WAIT_SELLER_SEND_SCHEDULE']);

// Шаг «Согласование дат отгрузки». Продавец инициирует график → покупатель подтверждает или
// контрпредлагает (итеративно). Календарь — кастомный мультивыбор; набор ровно requiredDays.
export default function ShipmentScheduleStep({ dealId, info }: ShipmentScheduleStepProps) {
  const { t } = useTranslation();
  const schedule = info.shipmentSchedule;
  const requiredDays = schedule?.requiredDays ?? 0;
  const range = scheduleRange(schedule?.start, requiredDays);
  const proposed = scheduleToIso(schedule?.dates);
  const ps = info.processingStep ?? '';
  const myRole: 'seller' | 'buyer' = info.isSeller ? 'seller' : 'buyer';

  const [draft, setDraft] = useState<string[]>(proposed);
  const [counterEditing, setCounterEditing] = useState(false);

  const suggest = useSuggestSchedule(dealId, myRole);
  const confirm = useConfirmSchedule(dealId);
  const acting = suggest.isPending || confirm.isPending;

  const sellerInitial = info.isSeller && SENDABLE_STEPS.has(ps);
  const myTurnToDecide =
    (info.isBuyer && ps === 'WAIT_BUYER_CONFIRM_SCHEDULE') || (info.isSeller && ps === 'WAIT_SELLER_CONFIRM_SCHEDULE');
  const editing = sellerInitial || (myTurnToDecide && counterEditing);
  const confirmed = ps === 'SHIPMENT_SCHEDULE_CONFIRMED';

  function whoLabel(by: string | undefined): string {
    if (by === 'SELLER') {
      return t('app.deals.role.SELLER');
    }
    if (by === 'CUSTOMER') {
      return t('app.deals.role.BUYER');
    }
    if (by === 'AGENT') {
      return t('app.deals.role.AGENT');
    }
    return '';
  }

  function waitingText(): string {
    if (ps === 'SHIPMENT_SCHEDULE_PROCESSING') {
      return t('app.deals.schedule.waiting.preparing');
    }
    if (info.isBuyer && SENDABLE_STEPS.has(ps)) {
      return t('app.deals.schedule.waiting.sellerForms');
    }
    if (info.isSeller && ps === 'WAIT_BUYER_CONFIRM_SCHEDULE') {
      return t('app.deals.schedule.waiting.buyerDecides');
    }
    if (info.isBuyer && ps === 'WAIT_SELLER_CONFIRM_SCHEDULE') {
      return t('app.deals.schedule.waiting.sellerDecides');
    }
    return t('app.deals.schedule.waiting.generic');
  }

  if (!range) {
    return (
      <Text fz="sm" c="gray.6">
        {t('app.deals.schedule.noRange')}
      </Text>
    );
  }

  // ── Подтверждено ────────────────────────────────────────────────────────────
  if (confirmed) {
    const period = schedulePeriod(schedule);
    return (
      <Stack gap="sm">
        <Group gap="xs" wrap="nowrap">
          <ThemeIcon size={32} radius="xl" variant="light" color="lime">
            <IconCalendarCheck size={18} />
          </ThemeIcon>
          <Text fz="sm" fw={500}>
            {t('app.deals.schedule.confirmedTitle')}
          </Text>
        </Group>
        {period ? (
          <Stack gap={0}>
            <Text fz="sm" fw={500}>
              {t('app.deals.schedule.lastShipment', { date: isoToRu(period.endIso) })}
            </Text>
            <Text fz="xs" c="gray.7">
              {t('app.deals.schedule.periodRange', {
                from: isoToRu(period.startIso),
                to: isoToRu(period.endIso),
                days: period.days,
              })}
            </Text>
          </Stack>
        ) : null}
        <ScheduleCalendar value={proposed} minIso={range.minIso} maxIso={range.maxIso} max={requiredDays} disabled confirmed />
        {schedule?.confirmedBy ? (
          <Text fz="xs" c="gray.7">
            {t('app.deals.schedule.confirmedBy', { who: whoLabel(schedule.confirmedBy) })}
          </Text>
        ) : null}
      </Stack>
    );
  }

  // ── Редактирование (продавец инициирует ИЛИ контрпредложение) ─────────────────
  if (editing) {
    const canSend = draft.length === requiredDays && requiredDays > 0;
    return (
      <Stack gap="sm">
        <Text fz="sm" c="gray.8">
          {t('app.deals.schedule.intro')}
        </Text>
        <ScheduleCalendar
          value={draft}
          onChange={setDraft}
          minIso={range.minIso}
          maxIso={range.maxIso}
          max={requiredDays}
        />
        <Text fz="sm" c={canSend ? 'gray.8' : 'red.7'}>
          {t('app.deals.schedule.counter', { count: draft.length, required: requiredDays })}
        </Text>
        <Group gap="sm" wrap="wrap">
          {myTurnToDecide ? (
            <Button variant="default" disabled={acting} onClick={() => setCounterEditing(false)}>
              {t('common.cancel')}
            </Button>
          ) : null}
          <Button
            leftSection={<IconSend size={16} />}
            disabled={!canSend || acting}
            loading={suggest.isPending}
            onClick={() => suggest.mutate(isoToSchedule(draft))}
          >
            {t('app.deals.schedule.send')}
          </Button>
        </Group>
      </Stack>
    );
  }

  // ── Решение (показан предложенный график, можно подтвердить или предложить свой) ─
  if (myTurnToDecide) {
    return (
      <Stack gap="sm">
        <Text fz="sm" c="gray.8">
          {t('app.deals.schedule.proposedTitle')}
        </Text>
        <ScheduleCalendar value={proposed} minIso={range.minIso} maxIso={range.maxIso} max={requiredDays} disabled />
        {schedule?.suggestedBy ? (
          <Text fz="xs" c="gray.7">
            {t('app.deals.schedule.suggestedBy', { who: whoLabel(schedule.suggestedBy) })}
          </Text>
        ) : null}
        <Divider />
        <Group gap="sm" wrap="wrap">
          <Button
            variant="default"
            leftSection={<IconPencil size={16} />}
            disabled={acting}
            onClick={() => {
              setDraft(proposed);
              setCounterEditing(true);
            }}
          >
            {t('app.deals.schedule.counterPropose')}
          </Button>
          <Button
            leftSection={<IconCalendarCheck size={16} />}
            disabled={acting}
            loading={confirm.isPending}
            onClick={() => confirm.mutate()}
          >
            {t('app.deals.schedule.confirm')}
          </Button>
        </Group>
      </Stack>
    );
  }

  // ── Ожидание (контрагент/система) ─────────────────────────────────────────────
  return (
    <Stack gap="sm">
      <Group gap="xs" wrap="nowrap">
        <ThemeIcon size={32} radius="xl" variant="light" color="brand">
          <IconClock size={18} />
        </ThemeIcon>
        <Text fz="sm">{waitingText()}</Text>
      </Group>
      {proposed.length > 0 ? (
        <ScheduleCalendar value={proposed} minIso={range.minIso} maxIso={range.maxIso} max={requiredDays} disabled />
      ) : null}
    </Stack>
  );
}
